import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, or, limit } from 'firebase/firestore';

const getOptimizedUrl = (url, width = 300) => {
  if (!url || !url.includes('cloudinary.com') || !url.includes('/upload/')) return url;
  const [base, resto] = url.split('/upload/');
  const segmentos = resto.split('/');
  const esTransformacion = /(^|,)(w_|h_|c_|q_|f_|e_|g_|b_|ar_|dpr_|fl_)/.test(segmentos[0]);
  if (esTransformacion) segmentos.shift();
  return `${base}/upload/w_${width},c_limit,q_auto,f_auto/${segmentos.join('/')}`;
};

const FALLBACK_IMG =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23ffffff"/%3E%3C/svg%3E';

/**
 * ProductosRelacionados
 * Muestra productos que comparten `categorias` o `tiposUsuario`
 * con el producto actual. Al tocar una card navega al detalle
 * de ese producto.
 *
 * Uso en ProductDetail:
 *   <ProductosRelacionados producto={producto} />
 */
const ProductosRelacionados = ({ producto }) => {
  const navigate = useNavigate();
  const [relacionados, setRelacionados]     = useState([]);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hayOverflow, setHayOverflow]       = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!producto) return;

    const categorias   = Array.isArray(producto.categorias)   ? producto.categorias   : [];
    const tiposUsuario = Array.isArray(producto.tiposUsuario) ? producto.tiposUsuario : [];
    // Incluir también el campo singular por compatibilidad
    if (producto.categoria && !categorias.includes(producto.categoria)) {
      categorias.push(producto.categoria);
    }

    if (categorias.length === 0 && tiposUsuario.length === 0) {
      setRelacionados([]);
      return;
    }

    const obtenerRelacionados = async () => {
      try {
        const productosRef = collection(db, 'catalogo');

        // Firestore permite máximo 10 valores en array-contains-any
        const condiciones = [];
        if (categorias.length > 0) {
          condiciones.push(where('categorias', 'array-contains-any', categorias.slice(0, 10)));
        }
        if (tiposUsuario.length > 0) {
          condiciones.push(where('tiposUsuario', 'array-contains-any', tiposUsuario.slice(0, 10)));
        }

        const q = condiciones.length > 1
          ? query(productosRef, or(...condiciones), limit(15))
          : query(productosRef, condiciones[0], limit(15));

        const snap = await getDocs(q);
        let docs = [];
        snap.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));

        // Excluir el producto actual, ocultos, y deduplicar
        const vistos = new Set();
        docs = docs.filter(p => {
          if (p.id === producto.id)  return false;
          if (p.visible === false)   return false;
          if (vistos.has(p.id))      return false;
          vistos.add(p.id);
          return true;
        });

        // Orden simple por afinidad: cuántos campos comparte
        const afinidad = (p) => {
          let score = 0;
          const cats  = Array.isArray(p.categorias)   ? p.categorias   : [];
          const tipos = Array.isArray(p.tiposUsuario) ? p.tiposUsuario : [];
          categorias.forEach(c => { if (cats.includes(c)) score += 2; });
          tiposUsuario.forEach(t => { if (tipos.includes(t)) score += 1; });
          return score;
        };
        docs.sort((a, b) => afinidad(b) - afinidad(a));

        setRelacionados(docs.slice(0, 10));
      } catch (error) {
        console.error('Error obteniendo relacionados:', error);
      }
    };

    obtenerRelacionados();
  }, [producto]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tieneOverflow = el.scrollWidth > el.clientWidth + 4;
    setHayOverflow(tieneOverflow);
    setCanScrollLeft(el.scrollLeft > 6);
    setCanScrollRight(tieneOverflow && (el.scrollLeft + el.clientWidth < el.scrollWidth - 6));
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkScroll) : null;
    if (ro) ro.observe(el);

    const timer = setTimeout(checkScroll, 150);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      if (ro) ro.disconnect();
      clearTimeout(timer);
    };
  }, [checkScroll, relacionados]);

  const scrollManual = (direction) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({ left: direction === 'next' ? 300 : -300, behavior: 'smooth' });
  };

  const irAProducto = (p) => {
    navigate(`/producto/${p.id}`, { state: { producto: p } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (relacionados.length === 0) return null;

  return (
    <div style={{
      marginTop: 'var(--space-6)',
      fontFamily: 'var(--font-body)',
    }}>

      {/* Título de sección */}
      <h2 style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--font-size-h3)',
        fontWeight: 'var(--font-weight-black)',
        color: 'var(--text-primary)',
        letterSpacing: 'var(--letter-spacing-tight)',
        textAlign: 'center',
        margin: '0 0 var(--space-4)',
      }}>
        También podría interesarte
      </h2>

      {/* Carrusel */}
      <div style={{ position: 'relative' }}>

        {canScrollLeft && (
          <button
            type="button"
            aria-label="Deslizar hacia la izquierda"
            onClick={() => scrollManual('prev')}
            className="rel-nav-btn"
            style={{ left: '2px' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="var(--color-verde)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <div
          ref={scrollRef}
          className="rel-scroll scroll-oculto"
          style={{
            justifyContent: hayOverflow ? 'flex-start' : 'center',
            overflowX: hayOverflow ? 'auto' : 'visible',
            scrollSnapType: hayOverflow ? 'x proximity' : 'none',
          }}
        >
          {relacionados.map((p) => {
            const tieneOferta = p.precioOferta > 0 && p.precioOferta < p.precio;
            const precioFinal = tieneOferta ? p.precioOferta : p.precio;
            return (
              <div
                key={p.id}
                className="rel-card"
                onClick={() => irAProducto(p)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && irAProducto(p)}
              >
                <div className="rel-img-wrap">
                  <img
                    src={getOptimizedUrl(p.imagenUrl, 300) || FALLBACK_IMG}
                    alt={p.nombre}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  />
                </div>
                <div className="rel-body">
                  <p className="rel-nombre">{p.nombre}</p>
                  <div className="rel-precios">
                    {tieneOferta && (
                      <span className="rel-precio-antes">${Number(p.precio).toFixed(2)}</span>
                    )}
                    <span className="rel-precio">${Number(precioFinal).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            type="button"
            aria-label="Deslizar hacia la derecha"
            onClick={() => scrollManual('next')}
            className="rel-nav-btn"
            style={{ right: '2px' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="var(--color-verde)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <style>{`
        .scroll-oculto::-webkit-scrollbar { display: none; }
        .scroll-oculto { -ms-overflow-style: none; scrollbar-width: none; }

        .rel-scroll {
          display: flex;
          gap: var(--space-3);
          padding: var(--space-2) var(--space-1);
          scroll-behavior: smooth;
        }

        .rel-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 0.5px solid var(--border-default);
          background-color: var(--color-blanco);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }

        .rel-card {
          min-width: 120px;
          max-width: 120px;
          flex-shrink: 0;
          scroll-snap-align: start;
          background: var(--color-blanco);
          border: 0.5px solid var(--border-default);
          border-radius: var(--radius-lg);
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          transition: transform var(--transition-fast), box-shadow var(--transition-fast);
        }

        .rel-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-sm);
        }

        .rel-img-wrap {
          width: 100%;
          aspect-ratio: 1 / 1;
          background: var(--color-blanco);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .rel-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: var(--space-1);
        }

        .rel-body {
          padding: var(--space-2);
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .rel-nombre {
          margin: 0;
          font-size: 11px;
          font-weight: var(--font-weight-regular);
          color: var(--text-primary);
          line-height: 1.3;
          min-height: calc(1.3em * 2);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .rel-precios {
          display: flex;
          align-items: baseline;
          gap: var(--space-1);
          flex-wrap: wrap;
        }

        .rel-precio-antes {
          font-size: 10px;
          color: var(--text-disabled);
          text-decoration: line-through;
        }

        .rel-precio {
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
          color: var(--text-primary);
        }
      `}</style>
    </div>
  );
};

export default ProductosRelacionados;
